import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ModalTransition } from '@atlaskit/modal-dialog';

import Button from 'kingpin/atoms/Button';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import DashboardsContext from 'contexts/DashboardsContext';
import ReportsContext from 'contexts/ReportsContext';
import { InsightsChatContext } from 'contextProviders/InsightsChatProvider';
import {
  InsightsStudioEditRecord,
  ProposedActionStatus,
} from 'contextProviders/InsightsChatProvider/conversationTypes';
import { InsightsContentContext } from 'contextProviders/InsightsContentProvider';
import {
  InsightsChartSpec,
  ProposedAction,
} from 'services/streamInsightsQuery';

import AddToExistingModal from './saveChart/AddToExistingModal';
import AddToWorkspaceModal from './saveChart/AddToWorkspaceModal';
import { AddTarget } from './saveChart/useCreateInsightsContent';

const Card = styled.div`
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid var(--color-border-secondary);
  background-color: var(--color-bg-secondary);
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
`;

const ChartList = styled.ul`
  margin: 0 0 12px;
  padding-left: 18px;
  color: var(--color-text-secondary);
`;

const Detail = styled.div`
  margin-bottom: 8px;
  color: var(--color-text-secondary);
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const Resolved = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--color-text-secondary);
`;

// A readable label for one proposed chart (the agent's title, else the metric /
// dimension labels the spec carries, else a generic fallback).
const chartLabel = (spec: InsightsChartSpec): string =>
  spec.title || spec.valueLabel || spec.categoryLabel || 'Chart';

// The confirm card for a proposed write. Nothing is written until the user
// confirms. Create kinds (dashboard/report/metric) commit straight from the
// proposal; add_to_existing (3b) / add_to_workspace (3c) resolve a destination —
// the entity being viewed by default, or one the user picks — before committing.
const InsightsProposedAction = ({
  messageId,
  action,
  status,
  studioEdits,
}: {
  messageId: string;
  action: ProposedAction;
  status: ProposedActionStatus;
  // The answer's Studio edits, which include a confirmed metric's block
  studioEdits?: InsightsStudioEditRecord[];
}) => {
  const {
    confirmProposedAction,
    cancelProposedAction,
    isCreatingContent,
    activeEntity,
    sendQuestion,
  } = useContext(InsightsChatContext);
  const { allDashboards } = useContext(DashboardsContext);
  const { availableReports } = useContext(ReportsContext);
  // In Studio a confirmed metric goes on the canvas, not into a chat chart
  const { getStudioBuilder } = useContext(InsightsContentContext);
  const isInStudio = !!getStudioBuilder();
  // The block a confirmed metric put on the canvas, which this card shows
  // (and can take back off)
  const blockEdit = studioEdits?.find((e) => e.editId === `edit_${action.id}`);
  const [isBlockRemoved, setIsBlockRemoved] = useState<boolean>(false);
  const isBlockAdded = !!blockEdit?.isApplied && !isBlockRemoved;
  const canRemoveBlock =
    isBlockAdded && !!getStudioBuilder()?.canUndo(`edit_${action.id}`);
  const removeBlock = () => {
    getStudioBuilder()?.undo(`edit_${action.id}`);
    setIsBlockRemoved(true);
  };
  const [isPickerOpen, setPickerOpen] = useState(false);
  const [isWorkspacePickerOpen, setWorkspacePickerOpen] = useState(false);

  const isMetric =
    action.kind === 'create_metric' || action.kind === 'create_compound_metric';
  const isAddToExisting = action.kind === 'add_to_existing';
  const isAddToWorkspace = action.kind === 'add_to_workspace';

  // Default destination for add_to_existing = the dashboard/report being viewed.
  const defaultTarget: AddTarget | undefined = useMemo(() => {
    if (!isAddToExisting || !activeEntity) {
      return undefined;
    }
    if (activeEntity.entityType === 'dashboard') {
      const entity = allDashboards.find((d) => d.id === activeEntity.entityId);
      return entity ? { kind: 'dashboard', entity } : undefined;
    }
    if (activeEntity.entityType === 'report') {
      const entity = availableReports.find(
        (r) => r.id === activeEntity.entityId,
      );
      return entity ? { kind: 'report', entity } : undefined;
    }
    return undefined;
  }, [isAddToExisting, activeEntity, allDashboards, availableReports]);

  if (status === 'created') {
    const createdText = () => {
      if (
        action.kind === 'create_metric' ||
        action.kind === 'create_compound_metric'
      ) {
        return isBlockAdded
          ? `Created the metric “${action.metric.name}” and added it to the canvas.`
          : `Created the metric “${action.metric.name}”.`;
      }
      if (action.kind === 'add_to_existing') {
        return 'Charts added.';
      }
      if (action.kind === 'add_to_workspace') {
        return 'Added to the workspace.';
      }
      return `Created the ${action.kind} “${action.title}”.`;
    };
    return (
      <Card data-testid="insights-proposed-action">
        <Resolved>
          <Icon
            icon="checkmark"
            width={14}
            height={14}
            color="var(--color-text-secondary)"
          />
          <Typography.Body type="Body 12">{createdText()}</Typography.Body>
          {canRemoveBlock && (
            <Button
              size="Small"
              type="Ghost"
              label="Undo"
              onClick={removeBlock}
              testId="insights-proposal-undo-block"
            />
          )}
        </Resolved>
        {isBlockAdded && blockEdit?.missing && (
          <Detail>
            <Typography.Body type="Body 12" color="var(--color-status-warning)">
              {blockEdit.missing}
            </Typography.Body>
          </Detail>
        )}
        {isMetric && !isBlockAdded && (
          <Actions style={{ marginTop: 8 }}>
            {isInStudio ? (
              <Button
                size="Small"
                type="Ghost"
                label="Add to canvas"
                isDisabled={isCreatingContent}
                onClick={() =>
                  sendQuestion(`Add “${action.metric.name}” to the canvas`)
                }
                testId="insights-proposal-add-to-canvas"
              />
            ) : (
              <Button
                size="Small"
                type="Ghost"
                label="Chart it"
                isDisabled={isCreatingContent}
                onClick={() =>
                  sendQuestion(
                    `Chart “${action.metric.name}” for the last 6 months`,
                  )
                }
                testId="insights-proposal-chart-it"
              />
            )}
          </Actions>
        )}
      </Card>
    );
  }

  if (status === 'cancelled') {
    return (
      <Card data-testid="insights-proposed-action">
        <Resolved>
          <Typography.Body type="Body 12">Cancelled</Typography.Body>
        </Resolved>
      </Card>
    );
  }

  // Metric proposals (3c) — a definition summary rather than a chart list.
  if (
    action.kind === 'create_metric' ||
    action.kind === 'create_compound_metric'
  ) {
    const { metric } = action;
    const summary =
      action.kind === 'create_metric'
        ? `${action.metric.aggFunc} of ${action.metric.field} · ${action.metric.dataset}`
        : `Formula combining ${action.metric.metricIds.length} metric${
            action.metric.metricIds.length === 1 ? '' : 's'
          }`;
    return (
      <Card data-testid="insights-proposed-action">
        <Title>
          <Icon
            icon="chart-outlined"
            width={14}
            height={14}
            color="var(--color-text-primary)"
          />
          <Typography.Body type="Body 13 Bold">
            {`Create metric “${metric.name}”`}
          </Typography.Body>
        </Title>
        <Detail>
          <Typography.Body type="Body 12">{summary}</Typography.Body>
        </Detail>
        {metric.description && (
          <Detail>
            <Typography.Body type="Body 12">
              {metric.description}
            </Typography.Body>
          </Detail>
        )}
        {action.studioBlock && isInStudio && (
          <Detail>
            <Typography.Body type="Body 12">
              {`Then adds “${action.studioBlock.summary}” to the canvas`}
            </Typography.Body>
          </Detail>
        )}
        <Actions>
          <Button
            size="Small"
            type="Ghost"
            label="Cancel"
            isDisabled={isCreatingContent}
            onClick={() => cancelProposedAction(messageId)}
            testId="insights-proposal-cancel"
          />
          <Button
            size="Small"
            type="Primary"
            label="Create metric"
            isLoading={isCreatingContent}
            isDisabled={isCreatingContent}
            onClick={() => confirmProposedAction(messageId)}
            testId="insights-proposal-confirm"
          />
        </Actions>
      </Card>
    );
  }

  // Chart proposals: create dashboard/report, add_to_existing, add_to_workspace.
  const chartCount = action.charts.length;
  const chartCountLabel = chartCount === 1 ? '1 chart' : `${chartCount} charts`;
  const headline = isAddToWorkspace
    ? `Add ${chartCountLabel} to a workspace`
    : isAddToExisting
      ? `Add ${chartCountLabel} to a dashboard or report`
      : `Create ${action.kind} “${action.title}”`;

  return (
    <Card data-testid="insights-proposed-action">
      <Title>
        <Icon
          icon={action.kind === 'report' ? 'chart-outlined' : 'dashboard'}
          width={14}
          height={14}
          color="var(--color-text-primary)"
        />
        <Typography.Body type="Body 13 Bold">{headline}</Typography.Body>
      </Title>
      <ChartList>
        {action.charts.map((chart, index) => (
          // Charts have no stable id; index is fine for this short static list.
          // eslint-disable-next-line react/no-array-index-key
          <li key={index}>
            <Typography.Body type="Body 12">
              {chartLabel(chart)}
            </Typography.Body>
          </li>
        ))}
      </ChartList>
      <Actions>
        <Button
          size="Small"
          type="Ghost"
          label="Cancel"
          isDisabled={isCreatingContent}
          onClick={() => cancelProposedAction(messageId)}
          testId="insights-proposal-cancel"
        />
        {isAddToWorkspace ? (
          <Button
            size="Small"
            type="Primary"
            label="Add to workspace"
            isDisabled={isCreatingContent}
            onClick={() => setWorkspacePickerOpen(true)}
            testId="insights-proposal-choose-workspace"
          />
        ) : isAddToExisting ? (
          <>
            {defaultTarget && (
              <Button
                size="Small"
                type="Primary"
                label={`Add to “${defaultTarget.entity.name}”`}
                isLoading={isCreatingContent}
                isDisabled={isCreatingContent}
                onClick={() => confirmProposedAction(messageId, defaultTarget)}
                testId="insights-proposal-confirm"
              />
            )}
            <Button
              size="Small"
              type={defaultTarget ? 'Ghost' : 'Primary'}
              label={
                defaultTarget
                  ? 'Choose a different destination'
                  : 'Choose destination'
              }
              isDisabled={isCreatingContent}
              onClick={() => setPickerOpen(true)}
              testId="insights-proposal-choose-destination"
            />
          </>
        ) : (
          <Button
            size="Small"
            type="Primary"
            label={`Create ${action.kind}`}
            isLoading={isCreatingContent}
            isDisabled={isCreatingContent}
            onClick={() => confirmProposedAction(messageId)}
            testId="insights-proposal-confirm"
          />
        )}
      </Actions>
      <ModalTransition>
        {isPickerOpen && (
          <AddToExistingModal
            chartCount={chartCount}
            isSaving={isCreatingContent}
            onPick={(target) => {
              setPickerOpen(false);
              confirmProposedAction(messageId, target);
            }}
            onClose={() => setPickerOpen(false)}
          />
        )}
        {isWorkspacePickerOpen && (
          <AddToWorkspaceModal
            chartCount={chartCount}
            isSaving={isCreatingContent}
            defaultTitle={
              action.kind === 'add_to_workspace' ? action.title : undefined
            }
            defaultWorkspaceId={
              activeEntity?.entityType === 'workspace'
                ? activeEntity.entityId
                : undefined
            }
            onPick={(target) => {
              setWorkspacePickerOpen(false);
              confirmProposedAction(messageId, target);
            }}
            onClose={() => setWorkspacePickerOpen(false)}
          />
        )}
      </ModalTransition>
    </Card>
  );
};

export default InsightsProposedAction;
